// plane api（app-api）的組織 / 授權 / 分享端點（SPEC #org-model、#organizations-112、#partners-302、#vms-web-909）。
// 一個帳號（SPEC #org-model-account）：沒有工作帳號；每個帳號自帶個人組織，另可建 team 組織；設備清單見 lib/myDevices.ts。
// 所有請求走 SDK 的 kinioFetch：401 → refresh；403 REAUTH_REQUIRED → 重新驗證對話框 → 重送。
import { kinioFetch, planeBase, type KinioHome, type KinioSession } from '@kinio/web-sdk';
import type { AppConfig } from './appConfig';
import { treeApi, type OrgNode, type OrgTreeItem } from './orgTreeApi';

/** 節點（wire）→ 既有選單用的 Site。 */
export function nodeToSite(o: Pick<OrgRef, 'org_id'>, n: Pick<OrgNode, 'node_id' | 'parent_node_id' | 'name' | 'level_label' | 'version'> & Partial<OrgNode> & Partial<OrgTreeItem>): Site {
  return {
    site_id: n.node_id,
    org_id: o.org_id,
    parent_site_id: n.parent_node_id ?? undefined,
    kind: n.level_label ?? '',
    name: n.name,
    geo: n.geo,
    timezone: n.timezone,
    status: n.status ?? 'active',
    version: n.version,
  };
}

export interface Plane {
  env: string;
  api: string;
}

/** 設定裡的 plane + session 家戶帶的 endpoints（去重）。 */
export function planesOf(cfg: AppConfig, s: KinioSession | null): Plane[] {
  const out = new Map<string, Plane>();
  for (const [env, api] of Object.entries(cfg.planes ?? {})) if (api) out.set(api, { env, api });
  for (const h of s?.homes ?? []) {
    const api = h.endpoints?.api;
    if (api && !out.has(api)) out.set(api, { env: h.env, api });
  }
  return [...out.values()];
}

/**
 * 頁面用的家戶（GET /v1/me/devices 的 orgs[].homes 與只經分享看到設備的家戶，lib/myDevices.ts scopeHomes）。
 * role = 家戶角色（個人組織依組織角色預設；team 組織以 GET /v1/homes/{id} 為準）；只經分享的家戶 role 是空字串。
 * 每台設備能做什麼看設備的 actions（VmsDevice.access），不看這裡。
 */
export interface ScopeHome extends KinioHome {
  source: 'own' | 'org' | 'shared';
  org_id?: string;
  org_kind?: 'personal' | 'team';
  site_id?: string;
  timezone?: string;
  /** 只經分享進來的家戶：看不到家戶名稱與成員；display_name 是分享者 */
  external: boolean;
  /** 這個家戶裡有任何一台我可以管理（管理動作，SPEC #device-permissions-layers） */
  can_manage: boolean;
}

// ---- 組織（SPEC #org-model-orgs、#org-model-membership） ----

/** GET /v1/me/orgs 的一筆（個人組織一定在第一筆）＋所在 plane。 */
/** 警報接手時限的一筆設定（分鐘數 null = 不設 / 沿用；節點 0 = 這個節點不設） */
export interface AlarmSlaRow {
  site_id?: string;
  security_minutes: number | null;
  status_minutes: number | null;
  version: number;
  updated_by_name?: string;
  updated_at?: string;
}

export interface OrgAlarmSla {
  default: AlarmSlaRow;
  sites: AlarmSlaRow[];
}

export interface MyOrg {
  org_id: string;
  kind: 'personal' | 'team';
  display_name: string;
  brand?: string;
  ecosystem_id?: string;
  business_kinds: string[];
  /** 平台審核通過、還沒過期的服務類營業種類（平台 H 起才有；舊後端 undefined → 退回 business_kinds） */
  verified_kinds?: string[];
  verification_expires_at?: string | null;
  status: string;
  /** owner / org_admin / member / guest / developer / factory_ops */
  role: string;
  /** 有效的組織層權限（owner / org_admin 隱含全部） */
  org_permissions: string[];
  service_roles: string[];
  is_owner: boolean;
  mfa_required: boolean;
  mfa_satisfied: boolean;
  /** null / mfa_required / org_suspended / ip_not_allowed / session_reauth */
  notice: string | null;
  /** 我在這個組織是值班操作台帳號（AUTH-08）→ 總覽一律用值班版面（SPEC #vms-web-827；舊後端 undefined = 沒開） */
  duty_mode?: boolean;
  member_count?: number;
  /** orgs.authz_version（轉移擁有權的 expected_version） */
  version: number;
  /** 能不能自己開通 Kinio Node（owner / org_admin / owner·org_admin 指派的 node_admin；個人組織 false；SPEC #provisioning-s3-8-org）；舊後端 undefined = 不能 */
  node_enroll?: boolean;
  /** 代建中（還沒完成接手）的安裝商組織、代建者、接手客戶（SPEC #kinio-kms-handover；M5-03）；不是代建中 → 沒有 */
  built_by_partner?: string;
  built_by_user_id?: string;
  handover_user_id?: string;
  /** 這個 org 在哪個 plane（前端補上）。 */
  api: string;
  env: string;
}

export async function meOrgs(p: Plane): Promise<MyOrg[]> {
  const r = await kinioFetch<{ orgs?: Omit<MyOrg, 'api' | 'env'>[] }>(planeBase(p.api) + '/v1/me/orgs');
  return (r?.orgs ?? []).map((o) => ({ ...o, business_kinds: o.business_kinds ?? [], org_permissions: o.org_permissions ?? [], service_roles: o.service_roles ?? [], api: p.api, env: p.env }));
}

/** 組織事務的管理者：owner 或 org_admin（SPEC #org-model-roles）。 */
export function isOrgAdmin(o: Pick<MyOrg, 'role'>): boolean {
  return o.role === 'owner' || o.role === 'org_admin';
}

export function hasOrgPerm(o: Pick<MyOrg, 'role' | 'org_permissions'>, perm: string): boolean {
  return isOrgAdmin(o) || (o.org_permissions ?? []).includes(perm);
}

/** 組織的路徑現在走得通嗎（要求 MFA / 停用 / IP / 登入時效不過 → 組織頁只顯示原因）。 */
export function orgUsable(o: Pick<MyOrg, 'notice'>): boolean {
  return !o.notice;
}

/**
 * 組織節點（舊名「場域」；wire 上是 node_id / parent_node_id，這裡保留 site_id 欄名給既有選單用，nodeToSite 轉）。
 * kind = 層級名稱原值（level_label；內建鍵 @floor… 由 lib/levels.ts 依語系翻）。
 */
export interface Site {
  site_id: string;
  org_id: string;
  parent_site_id?: string;
  kind: string;
  name: string;
  geo?: { lat?: number; lng?: number; address?: string; country?: string };
  timezone?: string;
  status: 'active' | 'archived';
  version: number;
}

export interface OrgHome {
  home_id: string;
  display_name: string;
  site_id: string;
  owner_user_id?: string;
  management_status: string;
  env: string;
  things: number;
  version: number;
}

/** GET /v1/orgs/{org}/members 的一筆（只列本組織的資料，SPEC #org-model-membership）。 */
export interface OrgMember {
  user_id: string;
  email?: string;
  display_name?: string;
  /** owner / org_admin / member / guest / developer / factory_ops */
  role: string;
  status: 'active' | 'suspended' | string;
  org_permissions: string[];
  service_roles?: string[];
  duty_mode?: boolean;
  version: number;
  joined_at: string;
  is_me?: boolean;
  /** 我能不能移出他（自己 = 離開；owner 不能離開） */
  can_remove?: boolean;
}

/** 待接受的邀請（GET /v1/orgs/{org}/invitations 只列 pending）。 */
export interface Invitation {
  invite_id: string;
  email: string;
  role: string;
  status?: string;
  invited_by?: string;
  /** 邀請人的名字（沒有就 email；平台 H 起才有） */
  invited_by_name?: string;
  expires_at: string;
  created_at?: string;
}

/** POST /v1/orgs/{org}/invitations：已有帳號 → 直接加入；沒有 → 邀請（invite_url 只在這次回應出現）。 */
export type InviteResult =
  | { status: 'joined'; user_id: string; org_id: string; role: string }
  | { status: 'invited'; invite_id: string; invitation: Invitation; token: string; invite_url: string };

/** 組織內可以邀請 / 指派的角色（owner 只經建立或轉移；developer / factory_ops 只限開發者組織）。 */
export function assignableRoles(o: Pick<MyOrg, 'role' | 'business_kinds'>): string[] {
  const out = ['member', 'guest'];
  if (o.role === 'owner') out.unshift('org_admin');
  if (o.business_kinds.includes('developer')) out.push('developer', 'factory_ops');
  return out;
}

export interface RoleTemplate {
  role_id: string;
  org_id?: string;
  name: string;
  description?: string;
  role_kind: string;
  actions: string[];
  org_permissions: string[];
  version: number;
  system: boolean;
  /** 用這個範本、還沒套用到目前版本的有效授權筆數（IAM-04） */
  pending_apply?: number;
}

export interface ResourceScope {
  mode: 'all' | 'list';
  /** device_ref 字串（通道 = `<parent>:chN`，SPEC #terms） */
  devices?: { device_ref: string }[];
}

export interface GrantTarget {
  device_ref: string;
  binding_id: string;
  historical: boolean;
}

/** 時段限制（IAM-06）：days 用 ISO 1 = 週一 … 7 = 週日；to < from 表示跨午夜。 */
export interface GrantSchedule {
  tz: string;
  windows: { days: number[]; from: string; to: string }[];
}

export interface SiteGrant {
  grant_id: string;
  org_id: string;
  site_id: string;
  home_id: string;
  user_id: string;
  actions: string[];
  resource_scope: ResourceScope;
  include_descendants: boolean;
  role_id?: string;
  role_version?: number;
  valid_from: string;
  expires_at: string;
  revoked_at?: string;
  granted_by: string;
  version: number;
  targets?: GrantTarget[];
  schedule?: GrantSchedule;
}

/** MyCam 軟體設備與 AI 的場域政策（AUTH-04；鍵照規格原名）。 */
export interface OrgDevicePolicy {
  'soft_device.require_attestation': boolean;
  'soft_device.allow_personal_account': boolean;
  'soft_device.lock_settings': boolean;
  'soft_device.allow_audio_record': boolean;
  'ai.byo_cloud.allowed': boolean;
}

/** SCIM token（plan SPEC #vms-web-sso-scim）：明文只在產生時回一次。 */
export interface ScimToken {
  token_id: string;
  created_by: string;
  created_at: string;
  last_used_at?: string;
}
export interface ScimInfo {
  scim_tokens: ScimToken[];
  endpoint: string;
  counts: { users: number; unmatched: number; groups: number; last_sync_at?: string };
}

export interface OrgSecurityPolicy {
  org_id: string;
  /** 要求兩步驟驗證：只擋進入這個組織的資源（SPEC #org-model-policy） */
  require_mfa: boolean;
  /** 登入時效（分鐘；null = 不限） */
  login_max_age_minutes: number | null;
  idle_timeout_minutes: number;
  max_sessions: number | null;
  ip_allowlist?: string[];
  device_policy: OrgDevicePolicy;
  /** 成員能不能用個人 API 金鑰存取本組織（plan SPEC #api-keys-issuers）：沒設過照組織種類（個人 allowed、團隊 denied） */
  api_keys?: 'allowed' | 'denied';
  /** 事件快照上雲（plan SPEC #events-snapshot-off；舊後端沒有 → 當作 true）。個人組織沒有這個開關 */
  snapshot_upload?: boolean;
  /** 企業 SSO（plan SPEC #org-model-policy「要求 SSO」、#vms-web-sso-gate）；舊後端沒有 → 當作 false / 28800 / [] */
  require_sso?: boolean;
  sso_max_age_seconds?: number;
  sso_break_glass_user_ids?: string[];
  version: number;
  updated_by?: string;
  updated_at?: string;
}

export interface OrgSecurityPolicyIn {
  expected_version: number;
  require_sso?: boolean;
  sso_max_age_seconds?: number;
  sso_break_glass_user_ids?: string[];
  require_mfa?: boolean;
  login_max_age_minutes?: number | null;
  idle_timeout_minutes?: number;
  max_sessions?: number | null;
  ip_allowlist?: string[];
  device_policy?: OrgDevicePolicy;
  api_keys?: 'allowed' | 'denied';
  snapshot_upload?: boolean;
}

export interface MemberAccess {
  member: OrgMember;
  home_roles: { home_id: string; display_name: string; role: string; site_id: string }[];
  grants: SiteGrant[];
}

export interface OwnerTransferResult {
  transfer_id?: string;
  home_id: string;
  mode: string;
  previous_owner_user_id?: string;
  owner_user_id: string;
  home_version: number;
  revoked_grants: number;
  unchanged?: boolean;
  idempotent?: boolean;
  management_status: string;
}

export interface AuditEvent {
  event_id: string;
  org_id?: string;
  home_id?: string;
  actor_user_id?: string;
  actor_realm?: string;
  action: string;
  resource: Record<string, unknown>;
  detail: Record<string, unknown>;
  ip?: string;
  created_at: string;
}

function orgUrl(o: Pick<MyOrg, 'api' | 'org_id'>, path: string): string {
  return `${planeBase(o.api)}/v1/orgs/${encodeURIComponent(o.org_id)}${path}`;
}

export type OrgRef = Pick<MyOrg, 'api' | 'org_id'>;

function homeUrl(api: string, homeId: string, path: string): string {
  return `${planeBase(api)}/v1/homes/${encodeURIComponent(homeId)}${path}`;
}

export const orgApi = {
  /**
   * 組織的全部節點（結構，平面列表）：/sites 已改名 /nodes（SPEC #org-tree-api，不留相容層）；
   * 節點清單是延遲載入（GET …/nodes?parent=），這裡一層一層翻完給舊的場域選單用（地圖、事件 site:、授權、設備位置）。
   * 節點數上限 20,000，這裡最多走 2,000 個（再多的組織這些選單改用結構樹挑）。
   */
  sites: async (o: OrgRef): Promise<Site[]> => {
    const out = new Map<string, Site>();
    const first = await treeApi.children(o);
    const rootId = first.root?.node_id ?? first.items.find((x) => x.kind === 'node')?.parent_node_id ?? '';
    if (first.root) out.set(first.root.node_id, nodeToSite(o, first.root));
    const queue: { items: OrgTreeItem[] }[] = [first];
    while (queue.length && out.size < 2000) {
      const { items } = queue.shift()!;
      for (const it of items) {
        if (it.kind !== 'node' || !it.node_id || out.has(it.node_id)) continue;
        out.set(it.node_id, nodeToSite(o, { ...it, node_id: it.node_id, parent_node_id: it.parent_node_id ?? rootId ?? null, level_label: it.level_label ?? null }));
        if (it.has_children !== false) queue.push(await treeApi.children(o, it.node_id));
      }
    }
    return [...out.values()];
  },
  homes: (o: OrgRef) =>
    kinioFetch<{ homes: (OrgHome & { node_id?: string })[] }>(orgUrl(o, '/homes')).then((r) => (r?.homes ?? []).map((h) => ({ ...h, site_id: h.node_id ?? h.site_id }))),
  /** 把組織成員加進這一戶或改他在這一戶的角色（同一個人再送一次 = 改角色；step-up；vms#33） */
  addHomeMember: (o: OrgRef, homeId: string, body: { user_id: string; role: 'admin' | 'member' | 'guest' }) =>
    kinioFetch(orgUrl(o, '/homes/' + encodeURIComponent(homeId) + '/members'), { method: 'POST', body }),
  /** 成員清單（任何 active 成員；翻完所有頁）。 */
  members: async (o: OrgRef) => {
    const out: OrgMember[] = [];
    let cursor = '';
    for (let i = 0; i < 50; i++) {
      const r = await kinioFetch<{ members: OrgMember[]; next_cursor?: string }>(orgUrl(o, '/members' + (cursor ? '?cursor=' + encodeURIComponent(cursor) : '')));
      out.push(...(r?.members ?? []));
      cursor = r?.next_cursor ?? '';
      if (!cursor) break;
    }
    return out;
  },
  /** 停用 / 恢復、組織層權限（owner / org_admin / org.sites；step-up）。離開 / 移出用 removeMember。 */
  memberStatus: (o: OrgRef, userId: string, body: { expected_version: number; status?: 'active' | 'suspended'; org_permissions?: string[] }) =>
    kinioFetch<OrgMember>(orgUrl(o, '/members/' + encodeURIComponent(userId) + '/status'), { method: 'PATCH', body }),
  /** 改組織角色（只有 owner 能給 / 拿掉 org_admin）。 */
  memberRole: (o: OrgRef, userId: string, role: string, expectedVersion?: number) =>
    kinioFetch<{ user_id: string; role: string; version: number }>(orgUrl(o, '/members/' + encodeURIComponent(userId) + '/role'), {
      method: 'PUT',
      body: { role, expected_version: expectedVersion },
    }),
  /** 移出成員或自己離開（userId = 'me'）；他是 team 組織家戶的管理人 → 409 OWNS_HOMES，確認沒有接任者後帶 unassignOwned。 */
  removeMember: (o: OrgRef, userId: string, unassignOwned = false) =>
    kinioFetch<{ org_id: string; user_id: string; left: boolean; removed: boolean; result?: Record<string, unknown> }>(
      orgUrl(o, '/members/' + encodeURIComponent(userId) + (unassignOwned ? '?unassign_owned=true' : '')),
      { method: 'DELETE' },
    ),
  securityPolicy: (o: OrgRef) => kinioFetch<OrgSecurityPolicy>(orgUrl(o, '/security-policy')),
  putSecurityPolicy: (o: OrgRef, body: OrgSecurityPolicyIn) => kinioFetch<OrgSecurityPolicy>(orgUrl(o, '/security-policy'), { method: 'PUT', body }),
  /** SCIM（owner / org_admin；產生 / 撤銷要 5 分鐘內重新驗證，SDK 會先走 step-up 再重送）。 */
  scim: (o: OrgRef) => kinioFetch<ScimInfo>(orgUrl(o, '/scim-tokens')),
  createScimToken: (o: OrgRef) => kinioFetch<{ scim_token: ScimToken; token: string; endpoint: string }>(orgUrl(o, '/scim-tokens'), { method: 'POST' }),
  revokeScimToken: (o: OrgRef, id: string) => kinioFetch<unknown>(orgUrl(o, '/scim-tokens/' + encodeURIComponent(id)), { method: 'DELETE' }),
  /** 警報接手時限（EVT-07，SPEC #vms-web-alarm-workbench）：組織預設＋節點覆寫 */
  alarmSla: (o: OrgRef) => kinioFetch<OrgAlarmSla>(orgUrl(o, '/vms/alarm-sla')),
  putAlarmSla: (o: OrgRef, body: { security_minutes: number | null; status_minutes: number | null; expected_version: number }) =>
    kinioFetch<AlarmSlaRow>(orgUrl(o, '/vms/alarm-sla'), { method: 'PUT', body }),
  putAlarmSlaSite: (o: OrgRef, siteId: string, body: { security_minutes: number | null; status_minutes: number | null; expected_version: number }) =>
    kinioFetch<AlarmSlaRow>(orgUrl(o, '/vms/alarm-sla/sites/' + encodeURIComponent(siteId)), { method: 'PUT', body }),
  deleteAlarmSlaSite: (o: OrgRef, siteId: string, version: number) =>
    kinioFetch(orgUrl(o, `/vms/alarm-sla/sites/${encodeURIComponent(siteId)}?expected_version=${version}`), { method: 'DELETE' }),
  memberAccess: (o: OrgRef, userId: string) => kinioFetch<MemberAccess>(orgUrl(o, '/members/' + encodeURIComponent(userId) + '/access')),
  ownerTransfer: (
    o: OrgRef,
    homeId: string,
    body: { mode: 'handover' | 'takeover'; target_user_id: string; expected_version: number; reason: string },
    idempotencyKey: string,
  ) =>
    kinioFetch<OwnerTransferResult>(orgUrl(o, '/homes/' + encodeURIComponent(homeId) + '/owner-transfers'), {
      method: 'POST',
      body,
      headers: { 'Idempotency-Key': idempotencyKey },
    }),
  invitations: (o: OrgRef) => kinioFetch<{ invitations: Invitation[] }>(orgUrl(o, '/invitations')).then((r) => r?.invitations ?? []),
  invite: (o: OrgRef, body: { email: string; role: string; handover?: boolean }) => kinioFetch<InviteResult>(orgUrl(o, '/invitations'), { method: 'POST', body }),
  revokeInvite: (o: OrgRef, id: string) => kinioFetch(orgUrl(o, '/invitations/' + encodeURIComponent(id)), { method: 'DELETE' }),
  roles: (o: OrgRef) => kinioFetch<{ roles: RoleTemplate[]; actions: string[] }>(orgUrl(o, '/vms/role-templates')),
  createRole: (o: OrgRef, body: { name: string; description?: string; actions: string[] }) =>
    kinioFetch<RoleTemplate>(orgUrl(o, '/vms/role-templates'), { method: 'POST', body }),
  updateRole: (o: OrgRef, roleId: string, body: { expected_version: number; name?: string; actions?: string[] }) =>
    kinioFetch<{ role: RoleTemplate; pending_apply: number }>(orgUrl(o, '/vms/role-templates/' + encodeURIComponent(roleId)), { method: 'PUT', body }),
  /** IAM-04：範本新增的動作明示套用到還在舊版的授權（step-up） */
  applyRole: (o: OrgRef, roleId: string, expectedVersion: number) =>
    kinioFetch<{ role: RoleTemplate; applied: number }>(orgUrl(o, '/vms/role-templates/' + encodeURIComponent(roleId) + '/apply'), {
      method: 'POST',
      body: { expected_version: expectedVersion },
    }),
  grants: (o: OrgRef, q: { user_id?: string; home_id?: string; node_id?: string; include_revoked?: boolean } = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== '' && v !== false) qs.set(k, String(v));
    return kinioFetch<{ grants: (SiteGrant & { node_id?: string })[] }>(orgUrl(o, '/grants' + (qs.size ? '?' + qs : ''))).then((r) => (r?.grants ?? []).map((g) => ({ ...g, site_id: g.node_id ?? g.site_id })));
  },
  createGrant: (
    o: OrgRef,
    siteId: string,
    body: {
      home_id: string;
      user_id: string;
      role_id?: string;
      actions?: string[];
      resource_scope: ResourceScope;
      include_descendants?: boolean;
      expires_at: string;
      schedule?: GrantSchedule;
    },
  ) => kinioFetch<SiteGrant>(orgUrl(o, '/nodes/' + encodeURIComponent(siteId) + '/grants'), { method: 'POST', body }),
  refreshGrant: (o: OrgRef, g: SiteGrant, body: { expires_at?: string } = {}) =>
    kinioFetch<SiteGrant>(orgUrl(o, `/nodes/${encodeURIComponent(g.site_id)}/grants/${encodeURIComponent(g.grant_id)}`), {
      method: 'PUT',
      body: { expected_version: g.version, ...body },
    }),
  revokeGrant: (o: OrgRef, g: SiteGrant) =>
    kinioFetch<SiteGrant>(orgUrl(o, `/nodes/${encodeURIComponent(g.site_id)}/grants/${encodeURIComponent(g.grant_id)}`), { method: 'DELETE' }),
  /** 這個 org 稽核裡出現過的動作（篩選下拉，AUDIT-01）。 */
  auditActions: (o: OrgRef) => kinioFetch<{ actions: string[] }>(orgUrl(o, '/audit/actions')).then((r) => r?.actions ?? []),
  /** CSV 匯出（AUDIT-02，org.audit；預設最近 30 天）。 */
  auditCsvUrl: (o: OrgRef) => orgUrl(o, '/audit.csv'),
  audit: (o: OrgRef, q: { cursor?: string; home_id?: string; actor?: string; action?: string; q?: string; limit?: string } = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) if (v) qs.set(k, v);
    return kinioFetch<{ events: AuditEvent[]; next_cursor?: string }>(orgUrl(o, '/audit' + (qs.size ? '?' + qs : '')));
  },
};

// ---- 組織本身（SPEC #org-model-create、#org-model-membership） ----

/** GET /v1/orgs/{org}/built-orgs 的一列。 */
export interface BuiltOrg {
  org_id: string;
  display_name: string;
  builder: { user_id: string; name: string };
  customer?: { user_id: string; name: string };
  /** 還有效的接手邀請（遮罩的 email） */
  invite_email?: string;
  owner_transferred: boolean;
  created_at: string;
}

export const orgsApi = {
  /** 建 team 組織（任何登入的帳號；建立者 = owner，同交易建根 site）。名稱 2–60 字；每人最多 5 個（個人組織不算）。 */
  create: (p: Plane, displayName: string, builtForPartner?: string) =>
    kinioFetch<{ org: Omit<MyOrg, 'api' | 'env'> }>(planeBase(p.api) + '/v1/orgs', {
      method: 'POST',
      body: builtForPartner ? { display_name: displayName, built_for: { partner_org_id: builtForPartner } } : { display_name: displayName },
    }),
  /** 安裝商「代建的客戶」：代建、還沒完成接手的組織（安裝商組織的 owner / org_admin；SPEC #kinio-kms-handover；M5-03）。 */
  builtOrgs: (o: OrgRef) => kinioFetch<{ org_id: string; built_orgs: BuiltOrg[]; count: number; max: number }>(orgUrl(o, '/built-orgs')),
  get: (o: OrgRef) => kinioFetch<Omit<MyOrg, 'api' | 'env'>>(orgUrl(o, '')),
  /** 改組織名稱（owner / org_admin；2–60 字，422 INVALID_NAME；平台 H 起才有，舊後端 404 / 405）。 */
  rename: (o: OrgRef, displayName: string) =>
    kinioFetch<{ org: Omit<MyOrg, 'api' | 'env'> }>(orgUrl(o, ''), { method: 'PATCH', body: { display_name: displayName } }),
  /** 刪 team 組織（owner + step-up）；還有家戶 409 ORG_NOT_EMPTY（detail.homes）。 */
  remove: (o: OrgRef) => kinioFetch<{ org_id: string; deleted: boolean }>(orgUrl(o, ''), { method: 'DELETE' }),
  /** 轉移擁有權（team；owner + step-up）：對象要是 active org_admin；原 owner 變 org_admin。 */
  ownerTransfer: (o: OrgRef, targetUserId: string, expectedVersion: number) =>
    kinioFetch<{ org_id: string; previous_owner_user_id: string; owner_user_id: string; version: number }>(orgUrl(o, '/owner-transfer'), {
      method: 'POST',
      body: { target_user_id: targetUserId, expected_version: expectedVersion },
    }),
};

// ---- 接受邀請（SPEC #org-model-membership：一律對 email；已有帳號登入後 join，沒有帳號 join/register） ----

export interface JoinPreview {
  org: { org_id: string; kind: string; display_name: string; business_kinds?: string[] };
  role: string;
  email_masked: string;
  /** 登入的帳號 email = 受邀 email */
  email_match: boolean;
  invited_by?: string;
  expires_at: string;
  /** 已是成員時的角色 */
  member_role?: string;
}

/** join / preview 遇到個人分享的邀請（scope_kind = share）時，後端直接讓分享生效並回這個形狀。 */
export interface ShareJoined {
  status: 'joined';
  kind: 'share';
  share_id: string;
  home_id: string;
}

export function isShareJoined(x: unknown): x is ShareJoined {
  return !!x && (x as { kind?: string }).kind === 'share';
}

export const joinApi = {
  preview: (api: string, token: string) => kinioFetch<JoinPreview | ShareJoined>(planeBase(api) + '/v1/orgs/join/preview', { method: 'POST', body: { token } }),
  /** 接受；回應的 org_name / org_kind 是平台 H 起才有。 */
  join: (api: string, token: string) =>
    kinioFetch<{ status: 'joined'; org_id: string; org_name?: string; org_kind?: string; role: string } | ShareJoined>(planeBase(api) + '/v1/orgs/join', {
      method: 'POST',
      body: { token },
    }),
  /**
   * 沒有帳號的人憑邀請註冊（不用登入，直接 fetch；identity 視邀請連結為 email 已驗證）。
   * 這個帳號池已有同 email 的帳號 → 直接加入、existing_account = true（請他用原本的密碼登入）。
   */
  register: async (
    api: string,
    body: { token: string; password: string; display_name?: string; tos_version?: string },
    fetchImpl: typeof fetch = fetch,
  ): Promise<{ status: 'joined'; existing_account: boolean; email?: string; org_id?: string; org_name?: string; org_kind?: string; role?: string; kind?: string }> => {
    let res: Response;
    try {
      res = await fetchImpl(planeBase(api) + '/v1/orgs/join/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch {
      throw Object.assign(new Error('network'), { code: 'NETWORK', status: 0 });
    }
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok)
      throw Object.assign(new Error(String(data.message || data.error || 'HTTP ' + res.status)), {
        code: String(data.error || 'HTTP_' + res.status),
        status: res.status,
      });
    return data as { status: 'joined'; existing_account: boolean };
  },
};

/** 邀請連結裡的 token：`#t=<token>`（SPEC：token 在 fragment、不進 log），舊連結 `?token=` 也收。 */
export function inviteTokenFrom(loc: Pick<Location, 'hash' | 'search'>): string {
  const h = new URLSearchParams(loc.hash.replace(/^#/, ''));
  return (h.get('t') || new URLSearchParams(loc.search).get('token') || '').trim();
}

/**
 * 分享請求的代碼：直接輸入代碼，或貼上 deep link（https://link.kinio.rasitek.com/r/<code>?env=<plane>）。
 * env = 請求所屬的 plane（平台 H 起才帶）；沒帶就是 undefined，照現行用家戶所在的 plane。
 */
export function parseShareCode(input: string): { code: string; env?: string } {
  const s = input.trim();
  const m = s.match(/\/r\/([^/?#\s]+)(?:\?([^#\s]*))?/);
  if (!m) return { code: s };
  const env = new URLSearchParams(m[2] ?? '').get('env') ?? undefined;
  return { code: decodeURIComponent(m[1]), env: env || undefined };
}

/** VMS 自己的接受邀請頁（把 invite_url 的 token 帶到這個主機；token 一樣放 fragment）。 */
export function acceptInviteLink(origin: string, token: string, env?: string): string {
  return `${origin}/activate${env ? '?env=' + encodeURIComponent(env) : ''}#t=${encodeURIComponent(token)}`;
}

// ---- 分享（SPEC #org-model-shares、#partners-302；取代服務關係） ----

/** 分享給組織的用途（依組織的營業種類，SPEC #partners-80）。 */
export const PURPOSES_BY_KIND: Record<string, string[]> = {
  installer: ['install', 'maintenance'],
  security_provider: ['security_service', 'maintenance'],
  monitoring_center: ['monitoring'],
  property_manager: ['property'],
  care_provider: ['care'],
};
export const SERVICE_ROLES = ['operator', 'supervisor', 'technician'] as const;

/** 分享給個人可勾的資料動作（預設只勾「看」）。 */
export const PERSONAL_SHARE_ACTIONS = ['device.read', 'media.live', 'event.read', 'media.talk', 'recording.read', 'recording.export', 'device.control'] as const;
export const PERSONAL_SHARE_DEFAULT = ['device.read', 'media.live', 'event.read'];

/** 影像類動作（分享 / 同意時要 5 分鐘內重新驗證；分享給組織受 media_condition 約束）。 */
export const MEDIA_ACTIONS = ['media.live', 'media.talk', 'recording.read', 'recording.export'];

export interface AlarmType {
  class: string;
  armed_modes?: string[];
}
export interface ShareConditions {
  media_condition: 'always' | 'alarm_window' | 'armed';
  window_minutes?: number;
  alarm_types?: AlarmType[];
}

export interface ChannelScope {
  mode: 'all' | 'list';
  channels?: number[];
}

export interface ShareTarget {
  device_ref: string;
  binding_id?: string;
  historical?: boolean;
  channel_scope?: ChannelScope;
}

export interface ShareGrantee {
  kind: 'user' | 'org';
  email?: string;
  org_id?: string;
  display_name?: string;
  verified_kinds?: string[];
}

/** 家戶端看到的一筆分享（給個人或給組織）。 */
export interface HomeShare {
  share_id: string;
  home_id: string;
  grantee: ShareGrantee;
  /** personal 或分享給組織的用途 */
  purpose: string;
  scopes: string[];
  conditions?: ShareConditions;
  auto_include_new: boolean;
  history_from?: string;
  install_lan_mode: boolean;
  status: 'active' | 'suspended' | 'ended' | string;
  valid_from: string;
  /** 個人分享 null = 永久 */
  expires_at: string | null;
  ended_at?: string;
  end_reason?: string;
  version: number;
  created_at: string;
  targets: ShareTarget[];
}

export interface ShareTargetDetail extends ShareTarget {
  display_name?: string;
  room_name?: string;
  site_name?: string;
  bound_via_share: boolean;
  unbound?: boolean;
}

export interface ShareRequest {
  request_id: string;
  org_id: string;
  home_id?: string;
  purpose: string;
  scopes: string[];
  conditions: ShareConditions;
  duration_days: number;
  kind: 'new' | 'renewal';
  renew_share_id?: string;
  note?: string;
  expires_at: string;
  created_by: string;
  status: 'pending' | 'accepted' | 'declined' | 'expired' | 'canceled' | string;
  accepted_share_id?: string;
  created_at: string;
  /** 請求的設備範圍；list 時每台帶 name（只給我是家戶成員的設備；平台 H 起才有） */
  resource_scope?: { mode: 'all' | 'list'; devices?: { device_ref: string; name?: string }[] };
}

export interface ShareRequestPreview {
  request: ShareRequest;
  org: ShareGrantee;
  verified: boolean;
  purpose_default_scopes: string[];
  max_days: number;
}

export interface ShareAccess {
  access_id: string;
  share_id?: string;
  grantee_kind: 'user' | 'org';
  principal_kind: string;
  principal_id: string;
  actor_name?: string;
  action: string;
  device_ref?: string;
  result: string;
  created_at: string;
}

/** 別人分享給我的（GET /v1/me/shares）。 */
export interface MyShare {
  share_id: string;
  home_id: string;
  sharer: { user_id: string; display_name?: string };
  scopes: string[];
  devices: { device_ref: string; display_name?: string; channel_scope?: ChannelScope }[];
  status: string;
  valid_from: string;
  expires_at: string | null;
  created_at: string;
  version: number;
}

/** 分享給本組織的一筆（組織端；家戶名稱只在範圍含 home.profile.read 時給）。 */
export interface OrgReceivedShare {
  share_id: string;
  home_id: string;
  home_name?: string;
  purpose: string;
  scopes: string[];
  conditions: ShareConditions;
  auto_include_new: boolean;
  install_lan_mode: boolean;
  status: string;
  valid_from: string;
  expires_at: string | null;
  ended_at?: string;
  end_reason?: string;
  version: number;
  targets?: ShareTarget[];
}

export const shareApi = {
  list: (api: string, homeId: string, includeEnded = false) =>
    kinioFetch<{ shares: HomeShare[] }>(homeUrl(api, homeId, '/shares' + (includeEnded ? '?include_ended=1' : ''))).then((r) => r?.shares ?? []),
  /** 同 list，另帶 can_manage（呼叫者能分享 / 縮小 / 撤銷；平台 H 起才有，舊後端 undefined）。 */
  listWithManage: (api: string, homeId: string) =>
    kinioFetch<{ shares: HomeShare[]; can_manage?: boolean }>(homeUrl(api, homeId, '/shares')).then((r) => ({ shares: r?.shares ?? [], can_manage: r?.can_manage })),
  get: (api: string, homeId: string, shareId: string) =>
    kinioFetch<HomeShare & { target_details: ShareTargetDetail[]; bound_via_share: number }>(homeUrl(api, homeId, '/shares/' + encodeURIComponent(shareId))),
  /** 分享給個人：對方有帳號立即生效、沒有寄邀請；兩種都回「已送出」（不洩漏帳號存在與否）。含影像要 5 分鐘內重新驗證。 */
  toPerson: (
    api: string,
    homeId: string,
    body: { email: string; targets: ShareTarget[]; scopes: string[]; expires_at?: string; history_from?: string },
  ) =>
    kinioFetch<{ status: 'sent'; share: HomeShare }>(homeUrl(api, homeId, '/shares'), {
      method: 'POST',
      body: { grantee: { kind: 'user', email: body.email }, targets: body.targets, scopes: body.scopes, expires_at: body.expires_at, history_from: body.history_from },
    }),
  /** 組織出示的分享請求代碼：看內容（不改狀態）。 */
  preview: (api: string, code: string) =>
    kinioFetch<ShareRequestPreview>(`${planeBase(api)}/v1/share-requests/${encodeURIComponent(code)}/preview`, { method: 'POST' }),
  /** 同意分享給組織（只能 ≤ 請求範圍；含影像 / 布撤防 / 施工模式要重新驗證）。 */
  acceptOrg: (
    api: string,
    homeId: string,
    body: {
      code: string;
      scopes: string[];
      targets?: ShareTarget[];
      conditions?: ShareConditions;
      auto_include_new?: boolean;
      history_from?: string;
      install_lan_mode?: boolean;
      locale?: string;
    },
  ) => kinioFetch<HomeShare>(homeUrl(api, homeId, '/shares'), { method: 'POST', body }),
  /** 只縮：拿掉動作、拿掉設備、收緊條件、提早到期。 */
  shrink: (
    api: string,
    homeId: string,
    share: Pick<HomeShare, 'share_id' | 'version'>,
    body: { scopes?: string[]; drop_devices?: string[]; conditions?: ShareConditions; expires_at?: string },
  ) =>
    kinioFetch<HomeShare>(homeUrl(api, homeId, '/shares/' + encodeURIComponent(share.share_id)), {
      method: 'PATCH',
      body: { expected_version: share.version, ...body },
    }),
  revoke: (api: string, homeId: string, shareId: string) => kinioFetch<HomeShare>(homeUrl(api, homeId, '/shares/' + encodeURIComponent(shareId)), { method: 'DELETE' }),
  renew: (api: string, homeId: string, shareId: string, code: string) =>
    kinioFetch<HomeShare>(homeUrl(api, homeId, `/shares/${encodeURIComponent(shareId)}/renew`), { method: 'POST', body: { code } }),
  accessLog: (api: string, homeId: string, cursor?: string) =>
    kinioFetch<{ entries: ShareAccess[]; next_cursor?: string }>(homeUrl(api, homeId, '/share-access-log' + (cursor ? '?cursor=' + encodeURIComponent(cursor) : ''))),
  /** 別人分享給我的（我的 › 分享給我）。 */
  mine: (api: string) => kinioFetch<{ shares: MyShare[] }>(`${planeBase(api)}/v1/me/shares`).then((r) => r?.shares ?? []),
  /** 我被分享的存取紀錄（我的 › 安全 › 存取紀錄，SPEC #device-permissions-audit；平台 H 起才有）。 */
  myAccessLog: (api: string, cursor?: string) =>
    kinioFetch<{ entries: ShareAccess[]; next_cursor?: string }>(`${planeBase(api)}/v1/me/share-access-log` + (cursor ? '?cursor=' + encodeURIComponent(cursor) : '')),
  /** 我不要了。 */
  removeMine: (api: string, shareId: string) => kinioFetch(`${planeBase(api)}/v1/me/shares/${encodeURIComponent(shareId)}`, { method: 'DELETE' }),
};

/** 組織端：分享請求、收到的分享、交屋、成員的 service_roles 與值班模式（取代原 web/partner 主控台，SPEC #partners-302）。 */
export const orgShareApi = {
  createRequest: (o: OrgRef, body: { purpose: string; scopes?: string[]; duration_days?: number; note?: string; renew_share_id?: string; conditions?: Partial<ShareConditions> }) =>
    kinioFetch<{ request: ShareRequest; code: string; link: string }>(orgUrl(o, '/share-requests'), { method: 'POST', body }),
  requests: (o: OrgRef) => kinioFetch<{ requests: ShareRequest[] }>(orgUrl(o, '/share-requests')).then((r) => r?.requests ?? []),
  cancelRequest: (o: OrgRef, id: string) => kinioFetch(orgUrl(o, '/share-requests/' + encodeURIComponent(id)), { method: 'DELETE' }),
  received: (o: OrgRef) => kinioFetch<{ shares: OrgReceivedShare[] }>(orgUrl(o, '/shares')).then((r) => r?.shares ?? []),
  completeInstall: (o: OrgRef, shareId: string) =>
    kinioFetch<OrgReceivedShare & { notified: boolean }>(orgUrl(o, `/shares/${encodeURIComponent(shareId)}/complete-install`), { method: 'POST' }),
  end: (o: OrgRef, shareId: string) => kinioFetch<OrgReceivedShare>(orgUrl(o, `/shares/${encodeURIComponent(shareId)}`), { method: 'DELETE' }),
  setServiceRoles: (o: OrgRef, userId: string, roles: string[]) =>
    kinioFetch(orgUrl(o, `/members/${encodeURIComponent(userId)}/service-roles`), { method: 'PUT', body: { roles } }),
  setDutyMode: (o: OrgRef, userId: string, on: boolean) =>
    kinioFetch(orgUrl(o, `/members/${encodeURIComponent(userId)}/duty-mode`), { method: 'PUT', body: { on } }),
};

/** 能發分享請求的用途：審核通過的服務類營業種類（verified_kinds）；舊後端沒回 verified_kinds 時退回 business_kinds（伺服器照樣擋）。 */
export function sharePurposes(o: Pick<MyOrg, 'business_kinds' | 'verified_kinds'>): string[] {
  const kinds = o.verified_kinds ?? o.business_kinds;
  return [...new Set(kinds.flatMap((k) => PURPOSES_BY_KIND[k] ?? []))];
}

// ---- 設備庫存（DEV-15，SPEC #provisioning-inventory-api） ----
// app-api /v1/orgs/{org}/inventory…（platform!252）。registry 還沒部署新版時後端回 503 UNAVAILABLE，頁面照錯誤顯示。
export const INVENTORY_API_READY = true;

export interface InventoryDevice {
  device_ref: string;
  model: string;
  serial_no?: string;
  /** offline / online_unpaired / assigned / joined */
  status: string;
  online: boolean;
  last_seen_at?: string;
  source: string;
  added_at: string;
  order_ref?: string;
  assignment?: { home_id: string; home_name?: string; node_id?: string; assigned_by?: string; assigned_at?: string; last_error?: string };
  binding?: { home_id: string; home_name?: string };
  shipment?: { ship_ref: string; shipped_at: string };
}

export interface InventoryAssignResult {
  device_ref: string;
  /** assigned / joined / failed */
  status: string;
  error?: { error?: string; message?: string };
}

export const inventoryApi = {
  list: (o: OrgRef, q: { status?: string; model?: string; node_id?: string; q?: string; cursor?: string } = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) if (v) qs.set(k, v);
    return kinioFetch<{ devices: InventoryDevice[]; next_cursor?: string }>(orgUrl(o, '/inventory' + (qs.size ? '?' + qs : '')));
  },
  claim: (o: OrgRef, serial: string, orderRef: string) =>
    kinioFetch<{ device_ref: string; model: string; already_in_inventory: boolean }>(orgUrl(o, '/inventory/claims'), { method: 'POST', body: { serial, order_ref: orderRef } }),
  assign: (o: OrgRef, body: { home_id: string; node_id: string; device_refs: string[] }, idempotencyKey: string) =>
    kinioFetch<{ results: InventoryAssignResult[] }>(orgUrl(o, '/inventory/assignments'), {
      method: 'POST',
      body,
      headers: { 'Idempotency-Key': idempotencyKey },
    }),
  unassign: (o: OrgRef, device: string) => kinioFetch(orgUrl(o, '/inventory/assignments/' + encodeURIComponent(device)), { method: 'DELETE' }),
};

// 組織自己開通 Kinio Node（DEV-17，SPEC #provisioning-s3-8-org、#vms-web-node-enroll；platform node_enroll.go）。
export interface NodeEnrollRequest {
  id: string;
  mac: string;
  model: string;
  /** 機種目錄的 AID TYPE（AID 只分 TYPE、不分機種：開通時跟配發比對，SPEC #provisioning-type-quota）；舊版平台收的報到是空的 */
  type_code?: string;
  fw_version?: string;
  first_seen_at?: string;
  last_seen_at?: string;
  status: 'pending' | 'approved' | 'rejected' | (string & {});
  mac_conflict?: boolean;
  /** waiting（等節點報到）/ blinking（正在閃）/ missed（90 秒內沒報到）/ ""（沒按或已結束） */
  identify_state?: string;
  approved_at?: string | null;
  approved_via?: string | null;
  approved_role?: string | null;
  approved_by_name?: string;
  aid_tail?: string;
  aid_status?: string;
  note?: string | null;
  online?: boolean;
  last_online_at?: string;
}

export interface NodeEnrollQuota {
  /** 一律空的：額度只分 TYPE、不分機種（SPEC #provisioning-type-quota） */
  model: string;
  type_code: string;
  available: number;
  allocations: { allocation_id: string; created_at: string; available: number }[];
}

export interface NodeEnrollOverview {
  can_enroll: boolean;
  role: 'owner' | 'org_admin' | 'node_admin';
  enroll_url: string;
  quota: NodeEnrollQuota[];
  approved: NodeEnrollRequest[];
}

export const nodeEnrollApi = {
  overview: (o: OrgRef) => kinioFetch<NodeEnrollOverview>(orgUrl(o, '/node-enroll')),
  lookup: (o: OrgRef, q: string) => kinioFetch<{ request: NodeEnrollRequest }>(orgUrl(o, '/node-enroll/lookup'), { method: 'POST', body: { q } }),
  /** 讀單筆（畫面重讀閃燈狀態 / 開通後狀態；不寫稽核、不計次數）。指紋放 header，不進網址。 */
  get: (o: OrgRef, id: string, fp: string) =>
    kinioFetch<{ request: NodeEnrollRequest }>(orgUrl(o, '/node-enroll/' + encodeURIComponent(id)), { headers: { 'X-Kinio-Enroll-Fp': fp } }),
  identify: (o: OrgRef, id: string, fp: string) =>
    kinioFetch<{ request: NodeEnrollRequest }>(orgUrl(o, '/node-enroll/' + encodeURIComponent(id) + '/identify'), { method: 'POST', body: { fp } }),
  approve: (o: OrgRef, id: string, body: { fp: string; allocation_id: string; note?: string; confirm: true }) =>
    kinioFetch<{ request: NodeEnrollRequest; replayed: boolean }>(orgUrl(o, '/node-enroll/' + encodeURIComponent(id) + '/approve'), { method: 'POST', body }),
};

// 偵測到的設備（DEV-16，SPEC #vms-web-detected；platform detected.go）：ONVIF 探索 + Station 要位址的設備；鄰近發現平台端還沒有。
export const DETECTED_API_READY = true;

export interface DetectedDevice {
  detected_id: string;
  /** kinio / third_party / address_request */
  kind: string;
  device_ref?: string;
  model?: string;
  last6?: string;
  mac?: string;
  oui_vendor?: string;
  /** address_request：none / uplink_ip / uplink_arp */
  suspect?: string;
  seen_by: { home_id: string; home_name: string; device_ref: string; name?: string };
  sources: string[];
  seen_at: string;
  in_inventory: boolean;
  adopt: 'one_click' | 'device_button' | 'onvif' | 'approve_address' | (string & {});
}

export interface DetectedScan {
  until: string;
  nearby: boolean;
  onvif?: { gateways: number; failed: number; found: number };
}

export const detectedApi = {
  list: (o: OrgRef, node: string) => kinioFetch<{ detected_devices: DetectedDevice[] }>(orgUrl(o, `/nodes/${encodeURIComponent(node)}/detected-devices`)),
  scan: (o: OrgRef, node: string, onvif = false) =>
    kinioFetch<DetectedScan>(orgUrl(o, `/nodes/${encodeURIComponent(node)}/detected-devices/scan`), { method: 'PUT', body: onvif ? { onvif: true } : {} }),
  stop: (o: OrgRef, node: string) => kinioFetch(orgUrl(o, `/nodes/${encodeURIComponent(node)}/detected-devices/scan`), { method: 'DELETE' }),
  /** 收編（庫存內的 Kinio 設備）：200 joined / 202 assigned（設備上線就加入） */
  adopt: (o: OrgRef, node: string, detected: string, homeId: string, idemKey: string) =>
    kinioFetch<{ device_ref: string; status: 'joined' | 'assigned' }>(orgUrl(o, `/nodes/${encodeURIComponent(node)}/detected-devices/${encodeURIComponent(detected)}/adopt`), {
      method: 'POST',
      body: { home_id: homeId },
      headers: { 'Idempotency-Key': idemKey },
    }),
};

// ---- 地圖與平面圖（plan/vms-web-v0.md §5.2.2 / §5.2.3；platform cmd/app-api/vmsmaps.go） ----

export interface ApiPlacement {
  placement_id?: string;
  home_id: string;
  device_ref: string;
  floorplan_id?: string;
  x?: number;
  y?: number;
  lat?: number;
  lng?: number;
  heading_deg: number;
  fov_deg?: number;
  range_m?: number;
  version?: number;
}

export interface GeoTransform {
  points: { x: number; y: number; lat: number; lng: number }[];
  matrix: [number, number, number, number, number, number];
}

export interface ApiFloorplan {
  floorplan_id: string;
  org_id?: string;
  home_id?: string;
  site_id?: string;
  name: string;
  content_type: string;
  width: number;
  height: number;
  geo_transform?: GeoTransform;
  opacity: number;
  sort: number;
  version: number;
  placements: ApiPlacement[];
}

/** 平面圖擁有者：家戶或組織。 */
export type FloorplanOwner = { kind: 'home'; api: string; homeId: string; label: string } | { kind: 'org'; api: string; orgId: string; label: string };

export function ownerKey(o: FloorplanOwner): string {
  return o.kind === 'home' ? 'h:' + o.homeId : 'o:' + o.orgId;
}

function ownerUrl(o: FloorplanOwner, path: string): string {
  return o.kind === 'home' ? homeUrl(o.api, o.homeId, '/vms' + path) : orgUrl({ api: o.api, org_id: o.orgId }, '/vms' + path);
}

export interface HomeGeo {
  lat?: number;
  lng?: number;
  address?: string;
  country?: string;
  /** IANA；存在家戶上（ORG-02），空字串 = 清掉 */
  timezone?: string;
}

export interface UserPrefs {
  map_provider?: 'google' | 'baidu' | '';
  locale?: string;
  tz_display?: 'site' | 'browser' | '';
  default_layout_id?: string;
}

export const vmsApi = {
  floorplans: (o: FloorplanOwner) => kinioFetch<{ data: ApiFloorplan[]; can_write: boolean }>(ownerUrl(o, '/floorplans')),
  createFloorplan: (o: FloorplanOwner, file: Blob, name: string, width: number, height: number, siteId?: string) => {
    const fd = new FormData();
    fd.append('file', file, name);
    fd.append('name', name);
    fd.append('width', String(Math.round(width)));
    fd.append('height', String(Math.round(height)));
    if (siteId) fd.append('site_id', siteId);
    return kinioFetch<ApiFloorplan>(ownerUrl(o, '/floorplans'), { method: 'POST', body: fd });
  },
  floorplanImage: (o: FloorplanOwner, id: string) =>
    kinioFetch<{ url: string; expires_in_seconds: number }>(ownerUrl(o, `/floorplans/${encodeURIComponent(id)}/image`)),
  updateFloorplan: (
    o: FloorplanOwner,
    fp: Pick<ApiFloorplan, 'floorplan_id' | 'version'>,
    body: { name?: string; site_id?: string; geo_transform?: GeoTransform | null; opacity?: number; sort?: number; placements?: ApiPlacement[] },
  ) =>
    kinioFetch<ApiFloorplan>(ownerUrl(o, `/floorplans/${encodeURIComponent(fp.floorplan_id)}`), {
      method: 'PATCH',
      body: { expected_version: fp.version, ...body },
    }),
  deleteFloorplan: (o: FloorplanOwner, fp: Pick<ApiFloorplan, 'floorplan_id' | 'version'>) =>
    kinioFetch(ownerUrl(o, `/floorplans/${encodeURIComponent(fp.floorplan_id)}?expected_version=${fp.version}`), { method: 'DELETE' }),
  geoPlacements: (api: string, homeId: string) => kinioFetch<{ data: ApiPlacement[]; can_write: boolean }>(homeUrl(api, homeId, '/vms/placements')),
  putGeoPlacement: (
    api: string,
    homeId: string,
    deviceRef: string,
    body: { expected_version: number; lat: number; lng: number; heading_deg: number; fov_deg?: number; range_m?: number },
  ) => kinioFetch<ApiPlacement>(homeUrl(api, homeId, '/vms/placements/' + encodeURIComponent(deviceRef)), { method: 'PUT', body }),
  deleteGeoPlacement: (api: string, homeId: string, deviceRef: string, version: number) =>
    kinioFetch(homeUrl(api, homeId, `/vms/placements/${encodeURIComponent(deviceRef)}?expected_version=${version}`), { method: 'DELETE' }),
  homeGeo: (api: string, homeId: string) => kinioFetch<HomeGeo>(homeUrl(api, homeId, '/geo')),
  setHomeGeo: (api: string, homeId: string, geo: HomeGeo) => kinioFetch<HomeGeo>(homeUrl(api, homeId, '/geo'), { method: 'PUT', body: geo }),
  prefs: (api: string) => kinioFetch<UserPrefs>(`${planeBase(api)}/v1/me/vms/prefs`),
  setPrefs: (api: string, p: UserPrefs) => kinioFetch<UserPrefs>(`${planeBase(api)}/v1/me/vms/prefs`, { method: 'PUT', body: p }),
};

export interface DeviceLink {
  link_id: string;
  home_id: string;
  sensor_ref: string;
  device_ref: string;
  purposes: ('popup' | 'clip' | 'rule')[];
  version: number;
}

export const linkApi = {
  list: (api: string, homeId: string) => kinioFetch<{ data: DeviceLink[]; can_write: boolean }>(homeUrl(api, homeId, '/vms/device-links')),
  put: (api: string, homeId: string, sensor: string, body: { expected_version: number; cameras: string[]; purposes: string[] }) =>
    kinioFetch<{ data: DeviceLink[] }>(homeUrl(api, homeId, '/vms/device-links/' + encodeURIComponent(sensor)), { method: 'PUT', body }),
};

// ---- ONVIF 探索與納管（vms-web §5.4；platform cmd/app-api/onvif.go） ----

export interface DiscoveredCam {
  host: string;
  port: number;
  xaddr?: string;
  manufacturer?: string;
  model?: string;
  name?: string;
  mac?: string;
  endpoint?: string;
  evidence: ('E1' | 'E2' | 'E3' | 'E4')[];
  device_id?: string;
  channel?: number;
  device_ref?: string;
}

export interface DiscoveryResult {
  gateway: string;
  groups: { managed: DiscoveredCam[]; own: DiscoveredCam[]; suspect: DiscoveredCam[]; third_party: DiscoveredCam[] };
  duplicates: DiscoveredCam[];
}

export type OnvifAddResult = 'ok' | 'auth_failed' | 'timeout' | 'unreachable' | 'unsupported';

export const onvifApi = {
  discover: (api: string, homeId: string, gateway: string, timeout = 4) =>
    kinioFetch<DiscoveryResult>(homeUrl(api, homeId, '/vms/onvif/discoveries'), { method: 'POST', body: { gateway, timeout } }),
  add: (
    api: string,
    homeId: string,
    body: { gateway: string; host: string; port?: number; user: string; password: string; name?: string; suspect?: boolean; force_reason?: string },
  ) =>
    kinioFetch<{ result: OnvifAddResult; detail?: string; channel?: number; device_ref?: string; codec?: 'h264' | 'h265_only' }>(
      homeUrl(api, homeId, '/vms/onvif/devices'),
      {
        method: 'POST',
        body,
      },
    ),
  remove: (api: string, homeId: string, gateway: string, channel: number) =>
    kinioFetch(homeUrl(api, homeId, `/vms/onvif/devices/${channel}?gateway=${encodeURIComponent(gateway)}`), { method: 'DELETE' }),
};

// ---- Kinio 攝影機通道（SPEC #kinio-station-kinio-cam；platform cmd/app-api/kinio_channels.go） ----
// Station 的一路拉一台 Kinio 攝影機：授權算「設定的人」對攝影機的 media.live（不限同家戶）；本機網頁設不了，只能從這裡設。

export interface KinioChannel {
  channel: number;
  device_ref: string;
  assigned_by: string;
  /** 設定者是我 */
  mine: boolean;
  created_at: string;
  /** hub 現在會不會放行 */
  authorized: boolean;
  /** 不成立的原因碼：not_granted / org_mfa_required / ip_not_allowed / share_expired / station_rebound / device_unbound … */
  denied?: string;
}

export const kinioChannelApi = {
  list: (api: string, homeId: string, gateway: string) =>
    kinioFetch<{ data: KinioChannel[] }>(homeUrl(api, homeId, `/vms/kinio-channels?gateway=${encodeURIComponent(gateway)}`)),
  set: (api: string, homeId: string, body: { gateway: string; device_ref: string; channel?: number; name?: string }) =>
    kinioFetch<{ channel: number; device_ref: string }>(homeUrl(api, homeId, '/vms/kinio-channels'), { method: 'POST', body }),
  remove: (api: string, homeId: string, gateway: string, channel: number) =>
    kinioFetch(homeUrl(api, homeId, `/vms/kinio-channels/${channel}?gateway=${encodeURIComponent(gateway)}`), { method: 'DELETE' }),
};

// ---- PTZ（LIVE-06）：經閘道轉 ONVIF 雲台；同一路同時一個人（lease 15 秒，每個命令續約） ----

export type PtzAction =
  | 'up'
  | 'down'
  | 'left'
  | 'right'
  | 'upleft'
  | 'upright'
  | 'downleft'
  | 'downright'
  | 'zoomin'
  | 'zoomout'
  | 'stop'
  | 'home'
  | 'preset_goto'
  | 'preset_set'
  | 'preset_del'
  | 'release';

export interface PtzPreset {
  token: string;
  name: string;
}

export const ptzApi = {
  command: (api: string, homeId: string, body: { device_ref: string; action: PtzAction; speed?: number; preset?: string; name?: string; force?: boolean }) =>
    kinioFetch<{ lease?: { holder_role: string; expires_at: string; mine: boolean }; preset?: string } | null>(homeUrl(api, homeId, '/vms/ptz'), {
      method: 'POST',
      body,
    }),
  presets: (api: string, homeId: string, deviceRef: string) =>
    kinioFetch<{ data: PtzPreset[] }>(homeUrl(api, homeId, '/vms/ptz/presets?device_ref=' + encodeURIComponent(deviceRef))).then((r) => r?.data ?? []),
};

// ---- AI 運算提供者（§5.11.1）：AI box / NPU NVR，指派攝影機給它跑模型 ----

export interface AiCapacity {
  channels: number;
  fps_per_channel: number;
  decode?: string[];
  max_resolution?: string;
  models: string[];
}

export interface AiAssignment {
  assignment_id: string;
  home_id: string;
  provider_ref: string;
  /** 被分析的攝影機 */
  device_ref: string;
  /** local_channel {ch}（提供者自己的通道）| kinio_channel {device_ref, stream, delivery}（經攝影機 / parent 代理） */
  source: { kind: string; [k: string]: unknown };
  models: { model: string; params?: Record<string, unknown> }[];
  version: number;
  updated_at: string;
  /** providers 回的才有：pending（還沒套用）/ running / degraded / unreachable / rejected */
  status?: string;
  fps?: number;
  detail?: string;
}

export interface AiProvider {
  device_ref: string;
  name: string;
  online: boolean;
  capacity: AiCapacity;
  load: number;
  used: number;
  assignments: AiAssignment[];
}

export const aiApi = {
  providers: (api: string, homeId: string) => kinioFetch<{ data: AiProvider[] }>(homeUrl(api, homeId, '/vms/ai/providers')).then((r) => r?.data ?? []),
  assign: (api: string, homeId: string, body: { provider: string; device_ref: string; models: AiAssignment['models']; delivery?: 'continuous' | 'event' }) =>
    kinioFetch<AiAssignment>(homeUrl(api, homeId, '/vms/ai/assignments'), { method: 'POST', body }),
  update: (
    api: string,
    homeId: string,
    id: string,
    body: { expected_version: number; provider?: string; models?: AiAssignment['models']; delivery?: string },
  ) => kinioFetch<AiAssignment>(homeUrl(api, homeId, '/vms/ai/assignments/' + encodeURIComponent(id)), { method: 'PATCH', body }),
  remove: (api: string, homeId: string, id: string) => kinioFetch(homeUrl(api, homeId, '/vms/ai/assignments/' + encodeURIComponent(id)), { method: 'DELETE' }),
};

// ---- 全文檢索與書籤（§5.7.2） ----

export interface SearchDoc {
  event_id: string;
  home_id: string;
  device_ref: string;
  class: string;
  /** EVT-02 分級（伺服器投影時算；空 = 依 class 算，沒有布防資訊） */
  level?: string;
  key: string;
  occurred_at: string;
  has_clip: boolean;
  ai_tags: string[];
  payload?: Record<string, unknown>;
  /** 有開始 / 結束的事件：started / ended（search.event_docs.phase；同一次的配對用，SPEC #vms-web-290 第八輪） */
  phase?: string;
  /** 綁定 id（同一次的開始與結束同一個 binding_id） */
  binding_id?: string;
}

export interface Facet {
  value: string;
  count: number;
}

export interface SearchResponse {
  data: SearchDoc[];
  facets: { class: Facet[]; level: Facet[]; device: Facet[]; home: Facet[]; tag: Facet[]; hour: Facet[] };
  total: number;
  /** 命中超過上限：total / facet 只算最近 2 萬筆（UI 顯示「N+」） */
  capped?: boolean;
  next_cursor?: string;
  engine: string;
  warnings?: string[];
  took_ms: number;
}

export interface Bookmark {
  bookmark_id: string;
  home_id: string;
  device_ref: string;
  start_at: string;
  end_at?: string;
  title: string;
  note: string;
  visibility: 'home' | 'private';
  created_by: string;
}

export const searchApi = {
  events: (api: string, p: { q: string; homes: string[]; sort?: 'time' | 'relevance'; cursor?: string; tz?: string; limit?: number }) => {
    const qs = new URLSearchParams({ q: p.q, homes: p.homes.join(','), sort: p.sort ?? 'time', tz: p.tz ?? '', limit: String(p.limit ?? 50) });
    if (p.cursor) qs.set('cursor', p.cursor);
    return kinioFetch<SearchResponse>(`${planeBase(api)}/v1/vms/search/events?${qs}`);
  },
};

export const bookmarkApi = {
  list: (api: string, homeId: string, from?: string, to?: string) => {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    return kinioFetch<{ data: Bookmark[] }>(homeUrl(api, homeId, '/vms/bookmarks' + (qs.size ? '?' + qs : ''))).then((r) => r?.data ?? []);
  },
  create: (
    api: string,
    homeId: string,
    body: { device_ref: string; start_at: string; end_at?: string; title: string; note?: string; visibility?: 'home' | 'private' },
  ) => kinioFetch<Bookmark>(homeUrl(api, homeId, '/vms/bookmarks'), { method: 'POST', body }),
  remove: (api: string, homeId: string, id: string) => kinioFetch(homeUrl(api, homeId, '/vms/bookmarks/' + encodeURIComponent(id)), { method: 'DELETE' }),
};

// ---- 匯出與證據（§5.8） ----

export interface ExportSource {
  device_ref: string;
  from: string;
  to: string;
  /** cloud（預設）| local（NVR / Station 硬碟，設備上傳）| backup（異地備份副本） */
  source?: 'cloud' | 'local' | 'backup';
}

export interface ExportFile {
  name: string;
  device_ref: string;
  from: string;
  to: string;
  size: number;
  sha256: string;
  frames: number;
}

export interface ExportJob {
  job_id: string;
  home_id: string;
  created_by: string;
  reason: string;
  sources: ExportSource[];
  status: 'queued' | 'running' | 'done' | 'failed' | 'expired' | 'deleted';
  progress: number;
  error?: string;
  files: ExportFile[];
  key_id?: string;
  signed: boolean;
  created_at: string;
  finished_at?: string;
  expire_at?: string;
  /** 保存（SPEC #vms-web-export-archive）：free = 免費 30 天（算進組織免費總量）、archive = 長期保管；舊平台沒有 = free */
  retention?: 'free' | 'archive';
  /** 所有檔案大小加總（bytes，1 GB = 10⁹） */
  size_bytes?: number;
  /** 長期保管結帳中 */
  archive_hold_until?: string;
  deleted_at?: string;
  deleted_by?: string;
  delete_reason?: string;
}

/** 組織的免費保存用量與上限（GET …/vms/exports 的 quota；家戶所屬組織跨家戶加總）。 */
export interface ExportQuota {
  used_bytes: number;
  limit_bytes: number;
}

export interface ShareLink {
  link_id: string;
  job_id: string;
  has_password: boolean;
  expires_at: string;
  max_views?: number;
  views: number;
  revoked_at?: string;
  created_at: string;
  /** 建立者填的收件人（2026-10-17 起必填；舊連結沒有） */
  recipient?: string;
  /** 識別碼 = link_id 末 6 碼大寫，跟播放頁浮水印對得上 */
  code?: string;
}

/** 分享連結總表的一列（SPEC #vms-web-540 EXP-10；GET /v1/homes/{home}/vms/share-links）。 */
export interface HomeShareLink {
  link_id: string;
  code: string;
  job_id: string;
  recipient?: string;
  created_by: string;
  created_by_name?: string;
  created_at: string;
  /** 連結與匯出包到期取早的 */
  expires_at: string;
  max_views?: number;
  views: number;
  has_password: boolean;
  revoked_at?: string;
  status: 'active' | 'expired' | 'revoked' | 'used_up';
  sources: { device_ref: string; from: string; to: string }[];
}

export interface ShareCreated {
  link: ShareLink;
  token: string;
  /** 期限超過匯出包保存，被截到匯出包到期 */
  clamped?: boolean;
  export_expire_at?: string;
}

/** 為什麼不能建新匯出（SPEC #vms-web-export-prod）：組織沒開通 / 這個環境沒開 worker / 暫存空間不夠 / 問不到 worker。 */
export type ExportBlockReason = 'not_enabled' | 'worker_disabled' | 'tmp_space_low' | 'gateway_unreachable' | 'quota_exceeded';

/** 匯出列表帶的「現在能不能建」（GET …/vms/exports 的 create；舊平台沒有 = 當成可以）。 */
export interface ExportCreateState {
  allowed: boolean;
  reason?: ExportBlockReason;
}

export const exportApi = {
  list: (api: string, homeId: string) => kinioFetch<{ data: ExportJob[] }>(homeUrl(api, homeId, '/vms/exports')).then((r) => r?.data ?? []),
  /** 列表 + create 狀態（〔新增匯出〕能不能按，SPEC #vms-web-export-prod）。 */
  page: (api: string, homeId: string) =>
    kinioFetch<{ data: ExportJob[]; create?: ExportCreateState; quota?: ExportQuota }>(homeUrl(api, homeId, '/vms/exports')).then((r) => ({
      jobs: r?.data ?? [],
      create: r?.create ?? { allowed: true },
      quota: r?.quota,
    })),
  /** 長期保管結帳（step-up；付款權限同帳單；回 Stripe Checkout 網址，回來帶 ?archive=done&intent=）。 */
  archive: (api: string, homeId: string, job: string, body: { period: string; reason: string; return_url: string }) =>
    kinioFetch<ArchiveCheckout>(homeUrl(api, homeId, `/vms/exports/${encodeURIComponent(job)}/archive`), { method: 'POST', body }),
  /** 刪除匯出包（step-up；長期保管中的只有家戶 owner / admin；不退費，SPEC #vms-web-export-archive）。 */
  remove: (api: string, homeId: string, job: string, reason: string) =>
    kinioFetch(homeUrl(api, homeId, `/vms/exports/${encodeURIComponent(job)}`), { method: 'DELETE', body: { reason } }),
  create: (api: string, homeId: string, body: { sources: ExportSource[]; reason: string }) =>
    kinioFetch<ExportJob>(homeUrl(api, homeId, '/vms/exports'), { method: 'POST', body }),
  downloadUrl: (api: string, homeId: string, job: string) => homeUrl(api, homeId, `/vms/exports/${encodeURIComponent(job)}/download`),
  shareLinks: (api: string, homeId: string, job: string) =>
    kinioFetch<{ data: ShareLink[] }>(homeUrl(api, homeId, `/vms/exports/${encodeURIComponent(job)}/share-links`)).then((r) => r?.data ?? []),
  share: (api: string, homeId: string, job: string, body: { expires_in_seconds: number; recipient: string; password?: string; max_views?: number }) =>
    kinioFetch<ShareCreated>(homeUrl(api, homeId, `/vms/exports/${encodeURIComponent(job)}/share-links`), { method: 'POST', body }),
  /** 分享連結總表（EXP-10）：all = 連到期 / 撤銷 / 用完的一起列 */
  homeShareLinks: (api: string, homeId: string, all: boolean) =>
    kinioFetch<{ data: HomeShareLink[] }>(homeUrl(api, homeId, `/vms/share-links?status=${all ? 'all' : 'active'}`)).then((r) => r?.data ?? []),
  revoke: (api: string, homeId: string, job: string, link: string) =>
    kinioFetch(homeUrl(api, homeId, `/vms/exports/${encodeURIComponent(job)}/share-links/${encodeURIComponent(link)}`), { method: 'DELETE' }),
};

// ---- 方案與帳單：VMS 席次 / 雲端 AI（plan/vms-web-v0.md §5.9 / §5.10 / §5.17）----

/** 付費主體：org 或 personal home（api = 它所在的 plane）。 */
export interface BillingSubjectRef {
  api: string;
  kind: 'org' | 'home';
  id: string;
}

export interface VmsProduct {
  product_id: string;
  sku: string;
  /** export_archive = 證據包長期保管（sku archive.<5gb|20gb|50gb>.<90d|1y|3y>，一次性；在匯出頁買，SPEC #vms-web-export-archive） */
  kind: 'seat' | 'ai' | 'ai_pack' | 'export_archive';
  mode?: 'event' | 'continuous';
  retention_days?: number;
  analyses?: number;
  features: string[];
  currency: string;
  unit_amount: number;
  interval?: 'month' | 'year';
  display_name: string;
  draft_values: boolean;
}

export interface BillingSubscription {
  sub_id: string;
  items: { product_id: string; kind: string; quantity: number }[];
  state: 'pending' | 'active' | 'cancel_pending' | 'grace' | 'on_hold' | 'expired' | 'revoked';
  period_end?: string;
  grace_until?: string;
  auto_renew: boolean;
  environment: string;
  created_at: string;
}

export interface BillingSeat {
  seat_id: string;
  product_id: string;
  mode: 'event' | 'continuous';
  retention_days: number;
  source: 'subscription' | 'grant';
  source_id: string;
  status: string;
  authorizing: boolean;
  assigned_ref?: string;
  assigned_home_id?: string;
  assigned_at?: string;
  source_until?: string;
  usage_this_month?: { clips: number; bytes: number };
  /** 最近 1 小時實測上行（kbps）；CLOUD-03 用實測 */
  upload_kbps_last_hour?: number;
}

export interface AiEntitlement {
  active: boolean;
  features: string[];
  period: string;
  base: number;
  packs: number;
  limit: number;
  used: number;
  sources: string[];
  notified_80?: string;
  notified_100?: string;
}

export interface BillingOverview {
  subject: { subject_kind: 'org' | 'home'; subject_id: string };
  brand: string;
  homes: string[];
  store_configured: boolean;
  /** false = 雲端全時錄影功能暫停，全時席次先不能買（Eddy 2026-09-28） */
  continuous_available: boolean;
  products: VmsProduct[];
  subscriptions: BillingSubscription[];
  seats: BillingSeat[];
  ai: AiEntitlement;
  /** 證據包長期保管（active 與 pending，新到舊；舊平台沒有） */
  archives?: ExportArchive[];
}

/** billing.export_archives 一列（SPEC #vms-web-export-archive）。 */
export interface ExportArchive {
  archive_id: string;
  job_id: string;
  home_id: string;
  product_id: string;
  period_days: number;
  size_class: '5gb' | '20gb' | '50gb';
  intent_id: string;
  status: 'pending' | 'active' | 'expired' | 'revoked';
  starts_at?: string;
  ends_at?: string;
  created_by: string;
  reason: string;
  created_at: string;
}

/** 長期保管結帳的回應。 */
export interface ArchiveCheckout {
  intent_id: string;
  url: string;
  expires_at: string;
  archive_id: string;
  product_id: string;
  size_class: string;
  currency: string;
  unit_amount: number;
  expire_at_after: string;
}

function subjectUrl(s: BillingSubjectRef, path: string): string {
  return `${planeBase(s.api)}/v1/${s.kind === 'org' ? 'orgs' : 'homes'}/${encodeURIComponent(s.id)}${path}`;
}

export const billingApi = {
  overview: (s: BillingSubjectRef) => kinioFetch<BillingOverview>(subjectUrl(s, '/billing')),
  checkout: (s: BillingSubjectRef, body: { product_id: string; quantity: number; return_url: string }) =>
    kinioFetch<{ intent_id: string; url: string }>(subjectUrl(s, '/billing/checkout-sessions'), { method: 'POST', body }),
  checkoutStatus: (s: BillingSubjectRef, intent: string) =>
    kinioFetch<{ status: 'open' | 'completed' | 'expired' | 'failed'; result: string }>(
      subjectUrl(s, '/billing/checkout-sessions/' + encodeURIComponent(intent)),
    ),
  portal: (s: BillingSubjectRef, returnUrl: string) =>
    kinioFetch<{ url: string }>(subjectUrl(s, '/billing/portal-session'), { method: 'POST', body: { return_url: returnUrl } }),
  assign: (s: BillingSubjectRef, seatId: string, ref: string) =>
    kinioFetch<{ seat: BillingSeat; covered_by_plan: boolean }>(subjectUrl(s, `/billing/seats/${encodeURIComponent(seatId)}/assignment`), {
      method: 'PUT',
      body: { ref },
    }),
  unassign: (s: BillingSubjectRef, seatId: string) =>
    kinioFetch(subjectUrl(s, `/billing/seats/${encodeURIComponent(seatId)}/assignment`), { method: 'DELETE' }),
};

// ---- 異地備份（§5.12 BAK） ----

export type BackupKind = 'nas_s3' | 'nas_smb' | 'nas_nfs' | 'nvr';

export interface FaultDomain {
  site?: string;
  facility?: string;
  power?: string;
  network?: string;
}

export interface BackupTestStep {
  step: string;
  ok: boolean;
  ms: number;
  code?: string;
  message?: string;
}

export interface BackupTestResult {
  at: string;
  ok: boolean;
  category: 'ok' | 'auth' | 'network' | 'permission' | 'space' | 'config' | 'integrity' | 'unsupported' | 'unknown';
  message: string;
  steps: BackupTestStep[];
  took_ms: number;
}

export interface BackupTarget {
  target_id: string;
  home_id: string;
  org_id?: string;
  kind: BackupKind;
  name: string;
  endpoint?: string;
  bucket?: string;
  prefix?: string;
  region?: string;
  path_style: boolean;
  access_path: 'public' | 'via_device';
  proxy_ref?: string;
  nvr_ref?: string;
  quota_bytes: number;
  used_bytes: number;
  fault_domain: FaultDomain;
  status: 'untested' | 'ok' | 'failed' | 'disabled';
  last_test?: BackupTestResult | null;
  version: number;
  credentials: Record<string, string>;
  policies: number;
  can_manage: boolean;
}

export interface BackupCredIn {
  access_key_id?: string;
  secret_access_key?: string;
  username?: string;
  password?: string;
}

export interface BackupTargetIn {
  home_id?: string;
  kind: BackupKind;
  name: string;
  endpoint?: string;
  bucket?: string;
  prefix?: string;
  region?: string;
  path_style?: boolean;
  access_path?: 'public' | 'via_device';
  proxy_ref?: string;
  nvr_ref?: string;
  quota_bytes: number;
  fault_domain?: FaultDomain;
  credentials?: { write?: BackupCredIn; read?: BackupCredIn };
  expected_version?: number;
}

export interface BackupChannel {
  channel_ref: string;
  stream: 'main' | 'sub';
  recording_class?: 'all' | 'continuous' | 'event';
  bitrate_bps?: number;
  bitrate_source?: 'reported' | 'user' | 'assumed';
}

export interface BackupEstimate {
  channels: number;
  retention_days: number;
  total_bps: number;
  raw_bytes: number;
  overhead_bytes: number;
  margin_bytes: number;
  required_bytes: number;
  overhead_ratio: number;
  margin_ratio: number;
  assumed?: string[];
}

export interface FaultCheck {
  same_domain: boolean;
  shared?: string[];
  reason?: string;
}

export interface BackupCheck {
  estimate?: BackupEstimate;
  fault_check?: FaultCheck;
  offsite?: boolean;
  target_free_bytes?: number;
}

export interface BackupPolicy {
  policy_id: string;
  org_id?: string;
  source_home_id: string;
  source_nvr_ref?: string;
  target_id?: string;
  target_home_id: string;
  sources?: BackupChannel[];
  retention_days: number;
  quota_bytes: number;
  rate_limit_bps: number;
  offsite?: boolean;
  estimate?: BackupEstimate;
  policy_version: number;
  status: 'draft' | 'pending_approval' | 'validating' | 'active' | 'degraded' | 'paused' | 'revoked';
  status_reason?: string;
  needs_target_approval: boolean;
  source_approved_version?: number | null;
  target_approved_version?: number | null;
  activated_at?: string | null;
  version: number;
  /** 目的端視角只有數量 */
  channels?: number;
}

export interface ChannelCoverage {
  channel_ref: string;
  state: 'ok' | 'degraded' | 'not_recording' | 'partial' | 'unknown';
  protected_until?: string;
  observed_until?: string;
  lag_seconds: number;
  missing_ranges: { from: string; to: string; reason: string }[];
  pending_bytes: number;
  pending_count: number;
  oldest_pending_age_seconds: number;
  verified_count: number;
  verified_bytes: number;
  last_verified_at?: string;
}

export interface BackupPolicyView {
  policy: BackupPolicy;
  target: { target_id: string; kind?: BackupKind; name?: string; status?: string; quota_bytes?: number; used_bytes?: number };
  my_sides: ('source' | 'target')[];
  can_approve: ('source' | 'target')[];
  coverage?: ChannelCoverage[];
  health?: string;
  verified_bytes?: number;
  verified_count?: number;
  /** 各狀態的副本數（BAK-07：deleting / deletion_pending 看得到） */
  replica_states?: Record<string, number>;
  /** 目的端視角 */
  object_count?: number;
  stored_bytes?: number;
}

export interface BackupPolicyIn {
  source_home_id?: string;
  source_nvr_ref: string;
  target_id: string;
  channels: BackupChannel[];
  retention_days: number;
  quota_bytes?: number;
  rate_limit_bps?: number;
  source_fault_domain?: FaultDomain;
  site_confirmed?: boolean;
  dry_run?: boolean;
  draft?: boolean;
}

export interface RestoreJob {
  job_id: string;
  policy_id: string;
  target_nvr_ref: string;
  channels: string[];
  from: string;
  to: string;
  status: 'queued' | 'running' | 'done' | 'failed' | 'canceled';
  total: number;
  restored: string[];
  failures: { replica_id: string; error: string }[];
  created_at: string;
}

export const backupApi = {
  targets: (api: string, homeId: string) => kinioFetch<{ data: BackupTarget[] }>(homeUrl(api, homeId, '/vms/backup-targets')).then((r) => r?.data ?? []),
  createTarget: (api: string, homeId: string, body: BackupTargetIn) =>
    kinioFetch<BackupTarget>(homeUrl(api, homeId, '/vms/backup-targets'), { method: 'POST', body }),
  updateTarget: (api: string, homeId: string, id: string, body: Partial<BackupTargetIn> & { expected_version: number }) =>
    kinioFetch<BackupTarget>(homeUrl(api, homeId, '/vms/backup-targets/' + encodeURIComponent(id)), { method: 'PATCH', body }),
  deleteTarget: (api: string, homeId: string, id: string, version: number) =>
    kinioFetch(homeUrl(api, homeId, `/vms/backup-targets/${encodeURIComponent(id)}?expected_version=${version}`), { method: 'DELETE' }),
  testTarget: (api: string, homeId: string, id: string) =>
    kinioFetch<{ result: BackupTestResult; target: BackupTarget }>(homeUrl(api, homeId, '/vms/backup-targets/' + encodeURIComponent(id) + '/test'), {
      method: 'POST',
    }),
  testAdhoc: (api: string, homeId: string, body: BackupTargetIn) =>
    kinioFetch<{ result: BackupTestResult }>(homeUrl(api, homeId, '/vms/backup-targets/test'), { method: 'POST', body }),
  policies: (api: string, homeId: string) => kinioFetch<{ data: BackupPolicyView[] }>(homeUrl(api, homeId, '/vms/backup-policies')).then((r) => r?.data ?? []),
  createPolicy: (api: string, homeId: string, body: BackupPolicyIn) =>
    kinioFetch<{ policy?: BackupPolicy; check: BackupCheck; ok?: boolean; needs_target_approval?: boolean }>(homeUrl(api, homeId, '/vms/backup-policies'), {
      method: 'POST',
      body,
    }),
  approve: (api: string, homeId: string, id: string, body: { expected_policy_version: number; side?: 'source' | 'target' }) =>
    kinioFetch<BackupPolicyView>(homeUrl(api, homeId, '/vms/backup-policies/' + encodeURIComponent(id) + '/approvals'), { method: 'POST', body }),
  patchPolicy: (
    api: string,
    homeId: string,
    id: string,
    body: Partial<BackupPolicyIn> & { expected_version: number; action?: 'pause' | 'resume' | 'revoke' },
  ) => kinioFetch<BackupPolicyView>(homeUrl(api, homeId, '/vms/backup-policies/' + encodeURIComponent(id)), { method: 'PATCH', body }),
  restoreJobs: (api: string, homeId: string, policyId: string) =>
    kinioFetch<{ data: RestoreJob[] }>(homeUrl(api, homeId, '/vms/backup-policies/' + encodeURIComponent(policyId) + '/restore-jobs')).then(
      (r) => r?.data ?? [],
    ),
  createRestore: (api: string, homeId: string, policyId: string, body: { target_nvr_ref: string; channels?: string[]; from: string; to: string }) =>
    kinioFetch<RestoreJob>(homeUrl(api, homeId, '/vms/backup-policies/' + encodeURIComponent(policyId) + '/restore-jobs'), { method: 'POST', body }),
  cancelRestore: (api: string, homeId: string, policyId: string, jobId: string) =>
    kinioFetch<RestoreJob>(homeUrl(api, homeId, `/vms/backup-policies/${encodeURIComponent(policyId)}/restore-jobs/${encodeURIComponent(jobId)}`), {
      method: 'DELETE',
    }),
  purge: (api: string, homeId: string, policyId: string, expectedVersion: number) =>
    kinioFetch<{ deleting: number }>(homeUrl(api, homeId, '/vms/backup-policies/' + encodeURIComponent(policyId) + '/purge'), {
      method: 'POST',
      body: { expected_version: expectedVersion, confirm: policyId },
    }),
};

// ---- 雲端 AI 同意（plan/vms-web-v0.md §5.10）----

export interface AiConsent {
  consent_id: string;
  features: string[];
  excluded_refs: string[];
  /** 指派給本地 AI 提供者的攝影機也照送雲端（預設 false = 雲端跳過；SPEC #vms-web-laih-l1） */
  analyze_local_too: boolean;
  text_version: string;
  granted_by: string;
  granted_at: string;
  version: number;
}

export const aiCloudApi = {
  consent: (s: BillingSubjectRef) => kinioFetch<{ consent: AiConsent | null; text_version: string }>(subjectUrl(s, '/ai/consent')),
  grant: (s: BillingSubjectRef, body: { text_version: string; features: string[]; excluded_refs: string[] }) =>
    kinioFetch<{ consent: AiConsent }>(subjectUrl(s, '/ai/consent'), { method: 'PUT', body }),
  exclusions: (s: BillingSubjectRef, body: { excluded_refs: string[]; expected_version: number }) =>
    kinioFetch<{ consent: AiConsent }>(subjectUrl(s, '/ai/consent'), { method: 'PATCH', body }),
  /** 「本地與雲端都分析」（打開要 5 分鐘內重新驗證，SDK 自動跳重驗；SPEC #vms-web-laih-l1） */
  localToo: (s: BillingSubjectRef, body: { analyze_local_too: boolean; expected_version: number }) =>
    kinioFetch<{ consent: AiConsent }>(subjectUrl(s, '/ai/consent'), { method: 'PATCH', body }),
  revoke: (s: BillingSubjectRef) => kinioFetch(subjectUrl(s, '/ai/consent'), { method: 'DELETE' }),
  usage: (s: BillingSubjectRef) => kinioFetch<AiEntitlement>(subjectUrl(s, '/ai/usage')),
};

// ---- 使用者自帶 AI（plan/SPEC.md#byo-ai-api）：主體同雲端 AI；第一版一主體一把 key，回應只有 key_hint ----

export type ByoProvider = 'openai' | 'anthropic' | 'gemini';

export interface ByoCredential {
  id: string;
  provider: ByoProvider;
  model: string;
  label: string;
  runner: 'platform' | 'device';
  key_hint: string;
  status: 'active' | 'invalid' | 'disabled' | 'needs_reentry';
  max_per_hour: number;
  max_per_day: number;
  device_refs: string[];
  version: number;
  created_at: string;
  last_used_at: string | null;
  last_error_code: string | null;
}

export interface ByoUsage {
  month: string;
  calls: number;
  failures: Record<string, number>;
}

export const aiByoApi = {
  list: (s: BillingSubjectRef) => kinioFetch<{ credentials: ByoCredential[]; text_version: string }>(subjectUrl(s, '/ai/byo/credentials')),
  /** 新增（要 5 分鐘內重新驗證，SDK 自動跳重驗）：平台先用 key 打供應商的列模型端點驗證，回可選模型 */
  create: (s: BillingSubjectRef, body: { provider: ByoProvider; api_key: string; model?: string; consent_text_version: string }) =>
    kinioFetch<{ credential: ByoCredential; models: string[] }>(subjectUrl(s, '/ai/byo/credentials'), { method: 'POST', body }),
  update: (
    s: BillingSubjectRef,
    id: string,
    body: { expected_version: number; model?: string; max_per_hour?: number; max_per_day?: number; api_key?: string },
  ) => kinioFetch<{ credential: ByoCredential }>(subjectUrl(s, '/ai/byo/credentials/' + encodeURIComponent(id)), { method: 'PATCH', body }),
  remove: (s: BillingSubjectRef, id: string) => kinioFetch(subjectUrl(s, '/ai/byo/credentials/' + encodeURIComponent(id)), { method: 'DELETE' }),
  devices: (s: BillingSubjectRef, body: { expected_version: number; device_refs: string[] }) =>
    kinioFetch<{ credential: ByoCredential }>(subjectUrl(s, '/ai/byo/devices'), { method: 'PUT', body }),
  usage: (s: BillingSubjectRef) =>
    kinioFetch<{ usage: ByoUsage | null; max_per_hour?: number; max_per_day?: number }>(subjectUrl(s, '/ai/byo/usage')),
};

/** 家戶設備的韌體狀態（app-api GET /v1/homes/{id}/ota，成員才看得到；最多 50 台）。HEALTH-01 韌體合規用。 */
export interface FirmwareJob {
  job_id: string;
  from_version?: string;
  version: string;
  status: string;
  progress: number;
  trigger: string;
  reason?: string;
  created_at: string;
  finished_at?: string;
}
export interface HomeFirmwareStatus {
  device_ref: string;
  thing_id?: string;
  model?: string;
  current_version?: string;
  online?: boolean;
  update_available?: boolean;
  reason?: string;
  error?: string;
  settings?: { auto_update?: boolean; maintenance_window?: string; channel?: string };
  release?: { release_id?: string; version?: string; critical?: boolean };
  job?: FirmwareJob | null;
}
export const homeFirmware = (api: string, homeId: string) =>
  kinioFetch<{ things: HomeFirmwareStatus[] }>(`${planeBase(api)}/v1/homes/${encodeURIComponent(homeId)}/ota`).then((r) => r?.things ?? []);

/** 每台的韌體設定 / 立即更新 / 更新歷史（OTA 的 App API …/things/{device}/ota；設定與立即更新要家戶 owner）。DEV-09 韌體管理頁用。 */
const fwBase = (api: string, homeId: string, device: string) =>
  `${planeBase(api)}/v1/homes/${encodeURIComponent(homeId)}/things/${encodeURIComponent(device)}/ota`;
export const fwApi = {
  settings: (api: string, homeId: string, aid: string, body: { auto_update?: boolean; maintenance_window?: string }) =>
    kinioFetch(fwBase(api, homeId, aid) + '/settings', { method: 'PUT', body }),
  update: (api: string, homeId: string, aid: string) =>
    kinioFetch<{ started?: boolean; delivery?: string; reason?: string; version?: string }>(fwBase(api, homeId, aid) + '/update', { method: 'POST' }),
  history: (api: string, homeId: string, aid: string, limit = 100) =>
    kinioFetch<{ history: FirmwareJob[] }>(fwBase(api, homeId, aid) + `/history?limit=${limit}`).then((r) => r?.history ?? []),
};
